import React from 'react';

import { fetchLookSamples, fetchSiteLooks, type LookSample, type SiteLooks } from './art-style-api';
import { SITE_REGEN_POLL_MS } from './site-regen';
import type { SiteLooksStatus } from './site-looks';

export interface UseSiteLooks {
  status: SiteLooksStatus;
  /** The brand's catalog looks; null until read, and when they are not available. */
  looks: SiteLooks | null;
}

type Held = { tenant: number; looks: SiteLooks };

/**
 * The brand's catalog looks (`GET …/site-looks`, see `./site-looks`). Read once per brand, again whenever the brand's
 * site job moves (`siteKey`: its job and phase, which `useSiteRegen` follows from the job's frames, so a look's art or
 * what the site shows changes with it) and every {@link SITE_REGEN_POLL_MS} while a site job runs. A re-read that fails
 * keeps the last answer; only a failed first read is a failure (`unavailable` for a 404: the service has no catalog
 * looks, and the page works as before them).
 */
export const useSiteLooks = (tenantId: number | undefined, siteKey: string, siteRunning: boolean): UseSiteLooks => {
  const [held, setHeld] = React.useState<Held | null>(null);
  const [failed, setFailed] = React.useState<{ tenant: number; status: 'unavailable' | 'failed' } | null>(null);
  const tenantRef = React.useRef(tenantId);
  tenantRef.current = tenantId;
  const heldRef = React.useRef(held);
  heldRef.current = held;
  /** The newest read sent; an older one landing late is dropped. */
  const seqRef = React.useRef(0);

  const read = React.useCallback(async (tenant: number): Promise<void> => {
    seqRef.current += 1;
    const seq = seqRef.current;
    const result = await fetchSiteLooks(tenant);
    if (seq !== seqRef.current || tenantRef.current !== tenant) return;
    if (result.ok) {
      setHeld({ tenant, looks: result.value });
      setFailed(null);
      return;
    }
    if (heldRef.current?.tenant === tenant) return;
    setFailed({ tenant, status: result.reason === 'unavailable' ? 'unavailable' : 'failed' });
  }, []);

  // Syncs with the service: reads the brand's looks, again on a brand switch.
  const seenKeyRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    seenKeyRef.current = undefined;
    if (tenantId === undefined) return;
    void read(tenantId);
  }, [tenantId, read]);

  // Syncs with the brand's site job: each move after the first one seen re-reads (the first is the page's own read).
  React.useEffect(() => {
    if (tenantId === undefined) return;
    const seen = seenKeyRef.current;
    seenKeyRef.current = siteKey;
    // No job seen yet ('' while the site job's first read is out) is not a move either.
    if (!seen || seen === siteKey) return;
    void read(tenantId);
  }, [tenantId, siteKey, read]);

  // Syncs with the service while a site job runs (a frame can be lost).
  React.useEffect(() => {
    if (!siteRunning || tenantId === undefined) return undefined;
    const timer = window.setInterval(() => void read(tenantId), SITE_REGEN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [siteRunning, tenantId, read]);

  if (tenantId === undefined) return { status: 'unavailable', looks: null };
  if (held && held.tenant === tenantId) return { status: 'ok', looks: held.looks };
  if (failed && failed.tenant === tenantId) return { status: failed.status, looks: null };
  return { status: 'loading', looks: null };
};

export interface UseLookSamples {
  /** The brand's own art in the look; undefined until the first read lands. */
  samples: LookSample[] | undefined;
  /** The first read failed (nothing is held). */
  failed: boolean;
}

type HeldSamples = { key: string; samples: LookSample[] };

/** The brand's own art in one catalog look, read per look and again each time `readKey` changes (a draw ended). A
 *  re-read that fails keeps the last answer. */
export const useLookSamples = (tenantId: number | undefined, styleId: string, readKey: string): UseLookSamples => {
  const key = tenantId !== undefined && styleId ? `${tenantId}:${styleId}` : '';
  const [held, setHeld] = React.useState<HeldSamples | null>(null);
  const [failedKey, setFailedKey] = React.useState<string | null>(null);
  const heldRef = React.useRef(held);
  heldRef.current = held;

  // Syncs with the service: reads the look's samples, again for another look and when a draw ends.
  React.useEffect(() => {
    if (tenantId === undefined || !styleId) return undefined;
    const ctl = new AbortController();
    const readFor = `${tenantId}:${styleId}`;
    void fetchLookSamples(tenantId, styleId, ctl.signal).then((result) => {
      if (ctl.signal.aborted) return;
      if (result.ok) {
        setHeld({ key: readFor, samples: result.value });
        setFailedKey(null);
        return;
      }
      if (heldRef.current?.key !== readFor) setFailedKey(readFor);
    });
    return () => ctl.abort();
  }, [tenantId, styleId, readKey]);

  return {
    samples: held && held.key === key ? held.samples : undefined,
    failed: key !== '' && failedKey === key && !(held && held.key === key),
  };
};

export default useSiteLooks;
